import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import { formatDate } from './helpers';

/**
 * Maintenance du portefeuille — réservée à la direction.
 *
 * Deux opérations irréversibles : purger l'archive et renuméroter les projets.
 * Elles sont volontairement séparées du reste de l'application, précédées d'un
 * aperçu de ce qui va changer, et verrouillées par une confirmation tapée à la
 * main. On ne supprime pas trente projets sur un clic distrait.
 */
function MaintenancePanel({ onTermine }) {
  const [apercu, setApercu] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // 'purge' | 'renum'
  const [saisie, setSaisie] = useState('');

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get('/admin/maintenance')
      .then((res) => setApercu(res.data))
      .catch((err) => setErreur(err.response?.data?.message || 'Aperçu indisponible.'))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const executer = (operation) => {
    const route = operation === 'purge' ? '/admin/purger-archive' : '/admin/renumeroter-projets';
    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .post(route, { confirmer: true })
      .then((res) => {
        setMessage(res.data.message);
        setConfirmation(null);
        setSaisie('');
        charger();
        if (onTermine) onTermine();
      })
      .catch((err) => setErreur(err.response?.data?.message || "L'opération a échoué."))
      .finally(() => setEnvoi(false));
  };

  if (chargement) return <p>Chargement de l’aperçu...</p>;
  if (!apercu) return <div className="error-message">{erreur || 'Aperçu indisponible.'}</div>;

  const aRenumeroter = apercu.renumerotation.filter((r) => r.change);
  const lies = Object.entries(apercu.elements_lies).filter(([, n]) => n > 0);

  const libelles = {
    tasks: 'tâches', comments: 'commentaires', attachments: 'documents',
    notifications: 'notifications', activity: 'lignes de journal',
    expenses: 'dépenses', snapshots: 'relevés d’avancement',
  };

  return (
    <div>
      <div className="maintenance-avertissement">
        <strong>Ces deux opérations sont irréversibles.</strong> Lancez
        d’abord <code>DEPLOYER-SUR-AZURE.bat</code> ou <code>sauvegarder-donnees.ps1</code> pour
        disposer d’une sauvegarde récente : c’est le seul moyen de revenir en arrière.
      </div>

      {erreur && <div className="error-message" style={{ marginBottom: 12 }}>{erreur}</div>}
      {message && <div className="success-message" style={{ marginBottom: 12 }}>{message}</div>}

      {/* --- 1. Purge de l'archive ---------------------------------------- */}
      <section className="rapport-section">
        <h3 className="rapport-section-titre">1. Supprimer définitivement les projets archivés</h3>

        {apercu.archives.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'left' }}>Aucun projet archivé.</div>
        ) : (
          <>
            <table className="rapport-tableau">
              <thead>
                <tr>
                  <th>Réf.</th><th>Projet</th><th>Archivé le</th>
                  <th className="rapport-col-droite">Tâches</th>
                  <th className="rapport-col-droite">Documents</th>
                </tr>
              </thead>
              <tbody>
                {apercu.archives.map((p) => (
                  <tr key={p.id}>
                    <td className="rapport-ref">{p.reference}</td>
                    <td>{p.name}</td>
                    <td>{p.archived_at ? formatDate(p.archived_at) : '—'}</td>
                    <td className="rapport-col-droite">{p.taches || '—'}</td>
                    <td className="rapport-col-droite">{p.documents || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="charte-note">
              Seront également supprimés :{' '}
              {lies.length
                ? lies.map(([c, n]) => `${n} ${libelles[c] || c}`).join(', ')
                : 'aucun élément lié'}
              , ainsi que les fichiers déposés sur ces projets.
            </p>

            {confirmation !== 'purge' ? (
              <button className="btn btn-danger" onClick={() => { setConfirmation('purge'); setSaisie(''); }}>
                Supprimer ces {apercu.archives.length} projet(s)
              </button>
            ) : (
              <div className="maintenance-confirmation">
                <p style={{ marginBottom: 10 }}>
                  Pour confirmer la suppression définitive de{' '}
                  <strong>{apercu.archives.length} projet(s)</strong>, tapez <strong>SUPPRIMER</strong> :
                </p>
                <input
                  value={saisie}
                  onChange={(e) => setSaisie(e.target.value)}
                  placeholder="SUPPRIMER"
                  style={{ maxWidth: 220 }}
                />
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-danger"
                    disabled={saisie !== 'SUPPRIMER' || envoi}
                    onClick={() => executer('purge')}
                  >
                    {envoi ? 'Suppression...' : 'Confirmer la suppression'}
                  </button>
                  <button className="btn" onClick={() => { setConfirmation(null); setSaisie(''); }}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* --- 2. Renumérotation --------------------------------------------- */}
      <section className="rapport-section">
        <h3 className="rapport-section-titre">2. Remettre les références à ZGP-001</h3>

        {apercu.archives.length > 0 && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            Purgez d’abord l’archive : sinon les références supprimées laisseraient
            de nouveaux trous dans la numérotation.
          </div>
        )}

        {aRenumeroter.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'left' }}>
            Les références sont déjà continues à partir de ZGP-001.
          </div>
        ) : (
          <>
            <p className="charte-note" style={{ marginBottom: 10 }}>
              {aRenumeroter.length} projet(s) changeraient de référence :
            </p>
            <div className="import-apercu">
              <table className="rapport-tableau">
                <thead>
                  <tr><th>Actuelle</th><th>Nouvelle</th><th>Projet</th></tr>
                </thead>
                <tbody>
                  {aRenumeroter.map((r) => (
                    <tr key={r.id}>
                      <td className="rapport-ref">{r.ancienne}</td>
                      <td className="rapport-ref" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                        {r.nouvelle}
                      </td>
                      <td>{r.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="maintenance-avertissement" style={{ marginTop: 12 }}>
              Tous les rapports PDF, courriels et documents déjà diffusés qui citent
              une ancienne référence deviendront faux. Les numéros de tâches
              (TSK-…) ne changent pas.
            </div>

            {confirmation !== 'renum' ? (
              <button
                className="btn btn-danger"
                disabled={apercu.archives.length > 0}
                onClick={() => { setConfirmation('renum'); setSaisie(''); }}
              >
                Renuméroter {aRenumeroter.length} projet(s)
              </button>
            ) : (
              <div className="maintenance-confirmation">
                <p style={{ marginBottom: 10 }}>
                  Pour confirmer, tapez <strong>RENUMEROTER</strong> :
                </p>
                <input
                  value={saisie}
                  onChange={(e) => setSaisie(e.target.value)}
                  placeholder="RENUMEROTER"
                  style={{ maxWidth: 220 }}
                />
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-danger"
                    disabled={saisie !== 'RENUMEROTER' || envoi}
                    onClick={() => executer('renum')}
                  >
                    {envoi ? 'Renumérotation...' : 'Confirmer la renumérotation'}
                  </button>
                  <button className="btn" onClick={() => { setConfirmation(null); setSaisie(''); }}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export default MaintenancePanel;
